import { useState, useEffect } from 'react';
import {
  KeyRound,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Clock,
  Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

interface ApiKeysSectionProps {
  showToast: (type: 'success' | 'error', message: string) => void;
}

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  status: 'pending' | 'active' | 'revoked' | 'expired';
  scopes: string[];
  requested_at: string;
  approved_at: string | null;
  revoked_at: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  request_note: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  active: 'bg-green-500/10 text-green-600 border-green-500/20',
  revoked: 'bg-red-500/10 text-red-600 border-red-500/20',
  expired: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending Approval',
  active: 'Active',
  revoked: 'Revoked',
  expired: 'Expired',
};

export function ApiKeysSection({ showToast }: ApiKeysSectionProps) {
  const { organization, isOwner, isAdmin, profile } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const canRequestKeys = isOwner || isAdmin;

  useEffect(() => {
    if (organization?.id) {
      fetchApiKeys();
    }
  }, [organization?.id]);

  async function fetchApiKeys() {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('id, name, key_prefix, status, scopes, requested_at, approved_at, revoked_at, expires_at, last_used_at, request_note')
        .eq('organization_id', organization!.id)
        .order('requested_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch {
      showToast('error', 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestKey() {
    if (!keyName.trim()) {
      showToast('error', 'Please provide a name for the API key');
      return;
    }

    setRequesting(true);
    try {
      const { error } = await supabase
        .from('api_keys')
        .insert({
          organization_id: organization!.id,
          name: keyName.trim(),
          key_prefix: '--------',
          key_hash: 'pending',
          status: 'pending',
          requested_by: profile?.id,
          request_note: requestNote.trim() || null,
        });

      if (error) throw error;

      showToast('success', 'API key requested. You will be notified when it is approved.');
      setKeyName('');
      setRequestNote('');
      setShowRequestForm(false);
      fetchApiKeys();
    } catch {
      showToast('error', 'Failed to request API key');
    } finally {
      setRequesting(false);
    }
  }

  function copyPrefix(prefix: string, id: string) {
    navigator.clipboard.writeText(prefix);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            API Keys
          </h4>
          <p className="text-sm text-muted-foreground mt-1">
            Manage API keys for integrating with the BlazePOS platform.
            Keys must be approved by the BlazePOS team before they can be used.
          </p>
        </div>
        {canRequestKeys && !showRequestForm && (
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowRequestForm(true)}
          >
            <KeyRound className="h-4 w-4" />
            Request API Key
          </Button>
        )}
      </div>

      {showRequestForm && (
        <div className="border rounded-xl p-4 space-y-4 bg-muted/30">
          <h5 className="font-medium">Request a New API Key</h5>
          <div className="space-y-2">
            <Label htmlFor="keyName">Key Name</Label>
            <Input
              id="keyName"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="e.g. Production Integration, POS Sync"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="requestNote">Note (optional)</Label>
            <Textarea
              id="requestNote"
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              placeholder="Describe what you plan to use this key for..."
              rows={3}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="blaze"
              className="gap-2"
              onClick={handleRequestKey}
              disabled={requesting}
            >
              {requesting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              Submit Request
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowRequestForm(false);
                setKeyName('');
                setRequestNote('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {apiKeys.length === 0 ? (
        <div className="border rounded-xl p-8 text-center">
          <KeyRound className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">
            No API keys yet. Request one to get started with the BlazePOS API.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Visit{' '}
            <a
              href="https://docs.blazepointofsale.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              docs.blazepointofsale.com
            </a>
            {' '}for integration guides.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {apiKeys.map((key) => (
            <div
              key={key.id}
              className="border rounded-xl p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{key.name}</span>
                  <Badge variant="outline" className={STATUS_STYLES[key.status]}>
                    {STATUS_LABELS[key.status]}
                  </Badge>
                </div>
                {key.status === 'active' && key.key_prefix !== '--------' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-xs"
                    onClick={() => copyPrefix(key.key_prefix + '...', key.id)}
                  >
                    {copiedId === key.id ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                    {key.key_prefix}...
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Requested {formatDate(key.requested_at)}
                </span>
                {key.approved_at && (
                  <span>Approved {formatDate(key.approved_at)}</span>
                )}
                {key.last_used_at && (
                  <span>Last used {formatDate(key.last_used_at)}</span>
                )}
                {key.expires_at && (
                  <span>Expires {formatDate(key.expires_at)}</span>
                )}
                {key.revoked_at && (
                  <span className="flex items-center gap-1 text-red-500">
                    <Ban className="h-3 w-3" />
                    Revoked {formatDate(key.revoked_at)}
                  </span>
                )}
              </div>

              {key.scopes.length > 0 && (
                <div className="flex gap-1.5">
                  {key.scopes.map((scope) => (
                    <Badge key={scope} variant="secondary" className="text-xs">
                      {scope}
                    </Badge>
                  ))}
                </div>
              )}

              {key.status === 'pending' && key.request_note && (
                <p className="text-xs text-muted-foreground italic">
                  Note: {key.request_note}
                </p>
              )}

              {key.status === 'pending' && (
                <div className="flex items-center gap-2 text-xs text-yellow-600 bg-yellow-500/5 rounded-lg p-2">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>This key is awaiting approval from the BlazePOS team.</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
